/* ============================================================
   MC PRICE RANGE — dual-handle price slider for Horizon's price facet.

   The slider is the visible UI; it drives the two hidden, *named* inputs
   that Horizon's <price-facet-component> already serialises and submits.
   On release we write the values into those inputs and dispatch a single
   `change`, so the native pipeline (updatePriceFilterAndResults ->
   facetsForm.updateFilters) runs unchanged — URL params, AJAX results and
   history/back-forward all keep working.

   EVERYTHING IS DELEGATED FROM `document`. Nothing is bound per node and no
   state is cached, because after every filter update Horizon morphs the
   section: `copyAttributes` in morph.js drops attributes the server didn't
   send and `updateInput` re-seeds form state. An earlier version bound
   listeners per slider and re-synced through a MutationObserver, which broke
   in two ways once a filter was applied:
     1. `render()` writes the value-label text, and those labels sit inside the
        observed <facets-form-component>, so a drag fed itself:
        input -> render -> mutation -> observer -> re-seed inputs from the
        server values -> handle snapped back to the applied filter. With a
        filter live (say 0-404) every subsequent drag was pulled back to
        0-404, so the first selection appeared to stick forever.
     2. Anything that depended on re-binding after a morph was one missed
        observer notification away from a dead slider.
   Delegation removes both: a slider node that appears at any time — initial
   render, morph, drawer opened later — works immediately, and the morph's own
   `updateInput` (morph.js: `oldNode.type === 'range'` branch) already re-seeds
   the handles from the server, so no re-sync layer is needed at all.

   The fill bar's width is rendered server-side in price-filter.liquid, so the
   design survives a morph stripping inline styles and is correct before this
   file even loads.

   No dependencies. Guarded singleton.
   ============================================================ */
(function () {
  'use strict';
  if (window.__mcPriceRangeInit) return;
  window.__mcPriceRangeInit = true;

  var SUBMIT_DELAY = 260;

  function num(value, fallback) {
    var n = parseFloat(value);
    return isNaN(n) ? fallback : n;
  }

  /* Read a slider's parts on demand. Cheap, and always reflects the CURRENT
     DOM — which is the point, since the nodes may have just been morphed. */
  function read(root) {
    var p = {
      root: root,
      min: num(root.dataset.min, 0),
      max: num(root.dataset.max, 0),
      step: num(root.dataset.step, 1) || 1,
      symbol: root.dataset.symbol || '$',
      inMin: root.querySelector('[data-mc-price-input="min"]'),
      inMax: root.querySelector('[data-mc-price-input="max"]'),
      outMin: root.querySelector('[data-mc-price-min]'),
      outMax: root.querySelector('[data-mc-price-max]'),
      fill: root.querySelector('[data-mc-price-fill]'),
      slider: root.querySelector('.mc-price-range__slider'),
      // Horizon's real (named) inputs — the ones the facets form serialises.
      facetMin: root.querySelector('[data-mc-price-facet="min"]'),
      facetMax: root.querySelector('[data-mc-price-facet="max"]')
    };
    if (!p.inMin || !p.inMax || !p.facetMin || !p.facetMax || p.max <= p.min) return null;
    return p;
  }

  function rootFor(node) {
    return node && node.closest ? node.closest('[data-mc-price-range]') : null;
  }

  function money(p, value) {
    var n = Math.round(value);
    return p.symbol + (n.toLocaleString ? n.toLocaleString() : n);
  }

  function pct(p, value) {
    return ((value - p.min) / (p.max - p.min)) * 100;
  }

  function render(p) {
    var a = +p.inMin.value, b = +p.inMax.value;
    var textMin = money(p, a);
    var textMax = money(p, b) + (b >= p.max ? '+' : '');
    if (p.outMin && p.outMin.textContent !== textMin) p.outMin.textContent = textMin;
    if (p.outMax && p.outMax.textContent !== textMax) p.outMax.textContent = textMax;
    if (p.fill) {
      p.fill.style.left = pct(p, a) + '%';
      p.fill.style.right = (100 - pct(p, b)) + '%';
    }
    p.inMin.setAttribute('aria-valuenow', a);
    p.inMax.setAttribute('aria-valuenow', b);
  }

  /* Handle arbitration. Both inputs span the full track and only their thumbs
     take pointer events, so whichever input paints on top wins any overlap.
     A fixed order traps the other handle once the thumbs are within a
     thumb-width of each other: with `max` pinned just above `min`, every grab
     landed on `min` and the max handle could not be dragged back out. Hand the
     top slot to whichever handle is nearer the pointer, on hover, before the
     press. (CSS sets the resting order — max on top.) */
  function raise(p, which) {
    p.inMin.style.zIndex = which === 'min' ? '5' : '3';
    p.inMax.style.zIndex = which === 'min' ? '3' : '5';
  }

  function nearest(p, clientX) {
    var rect = p.slider.getBoundingClientRect();
    if (!rect.width) return 'max';
    var ratio = (clientX - rect.left) / rect.width;
    ratio = ratio < 0 ? 0 : ratio > 1 ? 1 : ratio;
    var at = p.min + (p.max - p.min) * ratio;
    var dMin = Math.abs(at - +p.inMin.value);
    var dMax = Math.abs(at - +p.inMax.value);
    // On a tie prefer the handle that still has room to travel.
    if (dMin === dMax) return +p.inMin.value <= p.min ? 'max' : 'min';
    return dMin < dMax ? 'min' : 'max';
  }

  /* A handle at a boundary clears its param — no gte at the bottom, no lte at
     the top (so the label reads "$X+" and results aren't over-filtered). */
  function submit(p) {
    var a = +p.inMin.value, b = +p.inMax.value;
    var newMin = a <= p.min ? '' : String(a);
    var newMax = b >= p.max ? '' : String(b);
    if (p.facetMin.value === newMin && p.facetMax.value === newMax) return;
    p.facetMin.value = newMin;
    p.facetMax.value = newMax;
    // Horizon's <price-facet-component on:change="/updatePriceFilterAndResults">
    // picks this up and runs the native filter pipeline.
    p.facetMax.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* Coalesce bursts into one request: holding an arrow key fires a `change` per
     keypress, and each one is a full AJAX refetch + section morph. */
  function scheduleSubmit(root) {
    if (root.__mcTimer) clearTimeout(root.__mcTimer);
    root.__mcTimer = setTimeout(function () {
      root.__mcTimer = null;
      var p = read(root);
      if (p) submit(p);
    }, SUBMIT_DELAY);
  }

  document.addEventListener('input', function (event) {
    var target = event.target;
    if (!target || !target.hasAttribute || !target.hasAttribute('data-mc-price-input')) return;
    var root = rootFor(target);
    var p = root && read(root);
    if (!p) return;

    // Keep the handles from crossing.
    if (target === p.inMin && +p.inMin.value > +p.inMax.value - p.step) {
      p.inMin.value = +p.inMax.value - p.step;
    } else if (target === p.inMax && +p.inMax.value < +p.inMin.value + p.step) {
      p.inMax.value = +p.inMin.value + p.step;
    }
    render(p);
  });

  /* Capture phase, so the raw range `change` is stopped BEFORE it reaches
     <price-facet-component> — left alone it would submit on its own (with a
     value we may still be clamping) and double-fire alongside ours. */
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.hasAttribute || !target.hasAttribute('data-mc-price-input')) return;
    var root = rootFor(target);
    if (!root) return;
    event.stopPropagation();
    scheduleSubmit(root);
  }, true);

  document.addEventListener('pointermove', function (event) {
    var slider = event.target && event.target.closest
      ? event.target.closest('.mc-price-range__slider')
      : null;
    if (!slider) return;
    var p = read(rootFor(slider));
    if (p && p.slider) raise(p, nearest(p, event.clientX));
  });

  document.addEventListener('pointerdown', function (event) {
    var slider = event.target && event.target.closest
      ? event.target.closest('.mc-price-range__slider')
      : null;
    if (!slider) return;
    var p = read(rootFor(slider));
    if (p && p.slider) raise(p, nearest(p, event.clientX));
  });
})();
